/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { checkboxSizes, CheckboxStyle } from "@insite/mobius/Checkbox";
import { StyledClickable } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import Check from "@monsma/XDL/Icons/Check";
import { xBaseTheme } from "@monsma/XDL/types";
import { css } from "styled-components";

checkboxSizes.default = {
    fontSize: 13,
    borderRadius: 4,
    iconSize: 17,
};

export default {
    defaultProps: {
        color: "common.ui4",
        iconProps: {
            src: Check,
            size: 9,
            css: css`
                height: 17px;
                width: 17px;
                justify-content: center;
            `,
        },
        typographyProps: {
            disabledColor: "text",
            css: css`
                word-break: normal;
                line-height: 16px;
            `,
        },
        css: css`
            align-items: flex-start;
            ${({ disable, disabled }: any) => (disable || disabled) && `opacity:0.33`};
            ${StyledClickable} {
                display: inline;
            }
            &:hover {
                span[role="checkbox"]:not([aria-disabled="true"]) {
                    border-color: ${getColor("common.ui3")};
                    &[aria-checked="true"],
                    &[aria-checked="mixed"] {
                        background: ${getColor("common.ui3")};
                    }
                }
            }
            span[role="checkbox"] {
                background: ${getColor("common.ui7")};
                border: 1px solid ${getColor("common.ui9")};

                & + label {
                    margin-left: 8px;
                }

                &[aria-disabled="true"] {
                    border: 1px solid ${getColor("common.ui9")};
                    &[aria-checked="true"],
                    &[aria-checked="mixed"] {
                        background: ${({ _color, theme }: any) => resolveColor(_color, theme)};
                        color: ${({ _color, theme }: any) => getContrastColor(_color, theme)};
                        border: 0;
                    }
                }

                &[aria-checked="false"] {
                    svg {
                        display: block;
                        visibility: hidden;
                    }
                }

                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: 14,
            lineHeight: "20px",
            weight: "bold",
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        errorProps: {
            size: 13,
            lineHeight: "22px",
            weight: "bold",
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            ${({ disabled }: any) => disabled && `opacity:0.33`};
            & > ${TypographyStyle as any} {
                padding: 0;
                &:first-child {
                    margin-bottom: 20px;
                }
                &:last-child {
                    margin-top: 10px;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 20px;
            }
            &.inline-group {
                display:flex;
                flex-direction: row;
                flex-wrap: wrap;
                margin: -5px -10px;
                & > ${TypographyStyle as any} {
                    flex-basis: 100%;
                    padding: 5px 10px;

                    &:first-child {
                        margin-bottom: 10px;
                    }
                    &:last-child {
                        padding-top: 0;
                    }
                }
                ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                   margin-top: 0px;
                }
                ${/* sc-selector */ CheckboxStyle} {
                    padding: 5px 10px;
                }
            }
        `,
    },
} as xBaseTheme["checkbox"];
